import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { arrayMove, List } from 'react-movable';
import fieldPreviewIcon from 'assets/images/icons/field_preview_Icon.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import trashGreyIcon from 'assets/images/icons/trash-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import TextField from 'components/form-components/TextField';
import { CallAPI } from 'actions/General';
import { useTranslation } from 'react-i18next';
import { validateSqlColumnName } from 'pages/forms/utils';

const AddOptionsSetsModal = ({ show, editId, onCancel, refreshList }) => {
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [values, setValues] = useState({
    title: '',
    sqlColumn: '',
  });
  const [orignalValues, setOrignalValues] = useState({
    title: '',
    sqlColumn: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [optionList, setOptionList] = useState([]);
  const [orignalOptionList, setOrignalOptionList] = useState([]);
  const [originalOptionsLookup, setOriginalOptionsLookup] = useState({});
  const [deletedIds, setDeletedIds] = useState([]);

  const handleChange = (field, value) => {
    if (field === 'sqlColumn' && !validateSqlColumnName(value)) {
      return;
    }
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
  };

  const addOptionToList = () => setOptionList([...optionList, { optionValue: '' }]);

  const removeOptionFromList = useCallback((index) => {
    setOptionList((prev) => {
      const removed = prev[index];
      if (removed._id) {
        setDeletedIds((d) => [...d, removed._id]);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleOptionChange = useCallback((index, field, value) => {
    setOptionList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleSubmit = async () => {
    const isValid = optionList.every((o) => o.optionValue?.trim() !== '');
    if (!isValid) {
      toast.error('All options must have value');
      return;
    }

    let payload;

    if (!editId) {
      const orderedOptions = optionList.map((o, i) => ({
        optionValue: o.optionValue,
        position: i,
      }));

      payload = {
        title: values.title,
        sqlColumn: values.sqlColumn.toLowerCase(),
        options: orderedOptions,
      };
    } else {
      const updatedOptions = [];
      for (let i = 0; i < optionList.length; i++) {
        const orignalOption = originalOptionsLookup[optionList[i]._id];
        const itrativeOption = optionList[i];
        if (
          itrativeOption.optionValue !== orignalOption?.optionValue ||
          i !== orignalOption?.position
        ) {
          updatedOptions.push({
            optionValue: itrativeOption.optionValue,
            position: i,
            ...(itrativeOption._id ? { formTopicSetOptionId: itrativeOption._id } : {}),
          });
        }
      }

      payload = {
        title: values.title,
        sqlColumn: values.sqlColumn.toLowerCase(),
        options: updatedOptions,
        deletedOptions: deletedIds,
      };
    }

    const apiType = editId ? 'UPDATE_FORMS_SET' : 'ADD_FORMS_SET';
    const successMsg = editId ? 'alert_success_form_set_updated' : 'alert_success_form_set_added';

    const result = await CallAPI(apiType, payload, editId, setSaveLoading);
    if (result?.status) {
      toast.success(t(successMsg));
      refreshList();
      onCancel();
    } else {
      toast.error(result.error);
    }
  };

  const fetchFormsSetDetail = async () => {
    const result = await CallAPI(`GET_FORMS_SET_DETAIL`, null, editId, setFetchLoading);
    if (result.status) {
      setValues({
        title: result.data.title,
        sqlColumn: result.data.sqlColumn,
      });
      setOrignalValues({
        title: result.data.title,
        sqlColumn: result.data.sqlColumn,
      });

      const sortedOptions = result.data.options.sort((a, b) => a.position - b.position);
      const list = [];
      const lookup = {};

      sortedOptions.forEach((o, index) => {
        lookup[o._id] = {
          optionValue: o.optionValue,
          position: index,
        };
        list.push({
          optionValue: o.optionValue,
          _id: o._id,
        });
      });
      setOriginalOptionsLookup(lookup);
      setOrignalOptionList(sortedOptions);
      setOptionList(list);
    }
  };

  useEffect(() => {
    if (editId) fetchFormsSetDetail();
  }, [editId]);

  const hasChanges = () => {
    if (deletedIds.length > 0) return true;
    if (orignalOptionList.length !== optionList.length) return true;

    const isSame = orignalOptionList.every((o) => {
      const iterativeOption = optionList[o.position];
      return iterativeOption._id === o._id && iterativeOption.optionValue === o.optionValue;
    });
    return !isSame;
  };

  return (
    <div>
      <AlertModal show={show} actionButtons={[]} noPadding>
        <div className='w-100'>
          <label className='w-100 px-4 py-3 border-bottom-grey-1-h-1 theme-size-1_3 text-black fw-medium'>
            {t('text_add_form_sets')}
          </label>

          {fetchLoading ? (
            <div className='text-center my-3'>
              <Spinner className='theme-text-green-1' />
            </div>
          ) : (
            <div className='d-flex flex-column gap-3 px-4 py-3'>
              <TextField
                value={values.title}
                name='title'
                classes='w-auto'
                handleChange={handleChange}
                placeholder='field_name'
                displayName='field_name'
                shrinkFieldOnly
              />

              <TextField
                value={values.sqlColumn}
                name='sqlColumn'
                classes='w-auto'
                handleChange={handleChange}
                placeholder='field_sql_column'
                displayName='field_sql_column'
                shrinkFieldOnly
                disabled={!!editId}
              />

              <div className='d-flex align-items-center gap-2'>
                <img src={plusIcon} />
                <label
                  className='theme-text-green-1 cursor-pointer'
                  onClick={() => addOptionToList()}
                >
                  {t('button_add_option')}
                </label>
              </div>
              {optionList.length > 0 && (
                <List
                  values={optionList}
                  onChange={({ oldIndex, newIndex }) =>
                    setOptionList(arrayMove(optionList, oldIndex, newIndex))
                  }
                  renderList={({ children, props, isDragged }) => (
                    <div
                      {...props}
                      style={{ padding: 0, cursor: isDragged ? 'grabbing' : undefined }}
                      className='d-flex flex-column gap-3'
                    >
                      {children}
                    </div>
                  )}
                  renderItem={({ value: option, props, index, isDragged }) => {
                    return (
                      <div
                        {...props}
                        // eslint-disable-next-line react/prop-types
                        key={props.key}
                        style={{
                          // eslint-disable-next-line react/prop-types
                          ...props.style,
                          zIndex: isDragged ? 99999 : undefined,
                        }}
                        className='d-flex justify-content-between align-items-center'
                      >
                        <img src={fieldPreviewIcon} className='cursor-pointer' />
                        <TextField
                          value={option.optionValue}
                          name='optionValue'
                          handleChange={(field, value) => handleOptionChange(index, field, value)}
                          placeholder='field_value'
                          shrinkFieldOnly
                        />
                        <img
                          src={trashGreyIcon}
                          role='button'
                          className='cursor-pointer'
                          onClick={() => removeOptionFromList(index)}
                        />
                      </div>
                    );
                  }}
                />
              )}
            </div>
          )}

          <div className='d-flex gap-2 justify-content-end p-3 border-top-grey-1-h-1'>
            <FormButton text={'button_cancel'} variant='white-1' onClick={onCancel} />
            <FormLoadingButton
              text={editId ? 'button_update' : 'button_create'}
              variant='green-1'
              loading={saveLoading}
              disabled={
                values.title.trim() === '' ||
                values.sqlColumn === '' ||
                optionList.length < 1 ||
                (editId && !hasChanges() && !(values.title.trim() !== orignalValues.title))
              }
              onClick={handleSubmit}
            />
          </div>
        </div>
      </AlertModal>
    </div>
  );
};

AddOptionsSetsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  editId: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  refreshList: PropTypes.func.isRequired,
};

export default AddOptionsSetsModal;
